import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  ChevronLeft,
  Globe,
  Download,
  ShieldCheck,
  ShieldX,
  RefreshCw,
  Loader2,
  Users,
  Clock,
  Search,
  ExternalLink,
} from 'lucide-react';
import { useReportFilters } from './useReportFilters';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

function fmtSecs(s) {
  const n = Math.round(s || 0);
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${n}s`;
}

export default function UsageReportPage() {
  const navigate = useNavigate();
  const {
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    selectedStaff,
    setSelectedStaff,
    staffList,
    timezone,
    setPreset,
  } = useReportFilters();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async ({ soft = false } = {}) => {
    if (!fromDate || !toDate) return;
    if (soft) setRefreshing(true);
    else setLoading(true);
    setLoadError('');
    try {
      const res = await axios.get(`${API}/admin/usage-reports/summary`, {
        params: {
          from_date: fromDate,
          to_date: toDate,
          staff_id: selectedStaff === 'all' ? undefined : selectedStaff,
        },
        withCredentials: true,
      });
      setData(res.data);
    } catch {
      setLoadError('Could not load usage data. Try refreshing.');
      toast.error('Failed to load usage report');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fromDate, toDate, selectedStaff]);

  useEffect(() => {
    load();
  }, [load]);

  const staffRows = (data?.staff_usage || []).filter((r) => {
    if (!search.trim()) return true;
    return (r.staff_name || '').toLowerCase().includes(search.trim().toLowerCase());
  });
  const topBlocked = (data?.top_blocked_apps || []).slice(0, 8);
  const topAllowed = (data?.top_allowed_apps || []).slice(0, 8);
  const filtersReady = Boolean(fromDate && toDate);

  return (
    <div className="relative pb-8" data-testid="usage-report-page">
      <div className="pointer-events-none absolute inset-x-0 -top-6 h-56 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.12),_transparent_60%)]" />

      <div className="relative space-y-5 md:space-y-6">
        <section className="att-fade-up overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-4 text-white sm:px-6 sm:py-5">
            <div className="flex flex-row flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/admin/reports')}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25 hover:bg-white/25"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="min-w-0">
                  <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Usage Report</h1>
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-indigo-50/90">
                    <Globe className="h-3.5 w-3.5" />
                    Daily app & site usage · {timezone}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => load({ soft: true })}
                disabled={loading || refreshing || !filtersReady}
                className="h-10 shrink-0 rounded-xl bg-white px-4 text-sm font-semibold text-indigo-700 shadow-sm hover:bg-indigo-50"
              >
                {loading || refreshing ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-1.5 h-4 w-4" />
                )}
                Refresh
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3 border-b border-slate-100 bg-slate-50/70 px-4 py-3.5 sm:px-6">
            <div>
              <Label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Quick
              </Label>
              <div className="flex gap-1">
                {[
                  ['today', 'Today'],
                  ['week', 'Week'],
                  ['month', 'Month'],
                ].map(([k, l]) => (
                  <Button
                    key={k}
                    size="sm"
                    variant="outline"
                    onClick={() => setPreset(k)}
                    className="h-9 rounded-xl border-slate-200 bg-white text-xs"
                  >
                    {l}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                From
              </Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-9 w-[9.5rem] rounded-xl border-slate-200 bg-white text-sm"
              />
            </div>
            <div>
              <Label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                To
              </Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-9 w-[9.5rem] rounded-xl border-slate-200 bg-white text-sm"
              />
            </div>
            <div>
              <Label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Staff
              </Label>
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger className="h-9 w-44 rounded-xl border-slate-200 bg-white text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staffList.map((s) => (
                    <SelectItem key={s.staff_id} value={s.staff_id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {!filtersReady && (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
            Preparing filters…
          </div>
        )}

        {filtersReady && loading && !data && (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
            Loading usage…
          </div>
        )}

        {loadError && !data && (
          <div className="rounded-3xl border border-dashed border-rose-200 bg-rose-50/50 px-6 py-12 text-center">
            <p className="font-semibold text-rose-700">{loadError}</p>
            <Button
              onClick={() => load()}
              className="mt-4 h-10 rounded-xl bg-indigo-600 hover:bg-indigo-700"
            >
              Try again
            </Button>
          </div>
        )}

        {data && (
          <>
            <section
              className={cn(
                'att-fade-up grid grid-cols-2 gap-3 lg:grid-cols-4',
                (loading || refreshing) && 'opacity-60'
              )}
              style={{ animationDelay: '40ms' }}
            >
              {[
                {
                  label: 'Total usage',
                  value: fmtSecs(data.summary?.total_usage_seconds || 0),
                  soft: 'bg-sky-50 text-sky-700',
                  icon: Clock,
                },
                {
                  label: 'Allowed',
                  value: fmtSecs(data.summary?.allowed_seconds || 0),
                  soft: 'bg-indigo-50 text-indigo-700',
                  icon: ShieldCheck,
                },
                {
                  label: 'Blocked',
                  value: fmtSecs(data.summary?.blocked_seconds || 0),
                  soft: 'bg-rose-50 text-rose-700',
                  icon: ShieldX,
                },
                {
                  label: 'Staff tracked',
                  value: data.summary?.total_staff || 0,
                  soft: 'bg-purple-50 text-purple-700',
                  icon: Users,
                },
              ].map((k) => (
                <div
                  key={k.label}
                  className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                        {k.label}
                      </p>
                      <p className="mt-1.5 text-2xl font-bold tabular-nums text-slate-900">
                        {k.value}
                      </p>
                    </div>
                    <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', k.soft)}>
                      <k.icon className="h-5 w-5" />
                    </div>
                  </div>
                </div>
              ))}
            </section>

            <section className="att-fade-up grid gap-4 lg:grid-cols-2" style={{ animationDelay: '80ms' }}>
              <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:p-5">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900">
                  <ShieldX className="h-4 w-4 text-rose-500" />
                  Top blocked
                </h3>
                {topBlocked.length ? (
                  <div className="space-y-2.5">
                    {topBlocked.map((a, i) => {
                      const max = topBlocked[0]?.duration_seconds || 1;
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <span className="w-28 truncate text-sm text-slate-700 sm:w-36">
                            {a.app_name || a.domain}
                          </span>
                          <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-2 rounded-full bg-rose-400"
                              style={{ width: `${(a.duration_seconds / max) * 100}%` }}
                            />
                          </div>
                          <span className="w-12 text-right text-xs tabular-nums text-slate-500">
                            {fmtSecs(a.duration_seconds)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="py-6 text-center text-sm text-slate-400">No blocked usage</p>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm sm:p-5">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900">
                  <ShieldCheck className="h-4 w-4 text-indigo-500" />
                  Top allowed
                </h3>
                {topAllowed.length ? (
                  <div className="space-y-2.5">
                    {topAllowed.map((a, i) => {
                      const max = topAllowed[0]?.duration_seconds || 1;
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <span className="w-28 truncate text-sm text-slate-700 sm:w-36">
                            {a.app_name || a.domain}
                          </span>
                          <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-2 rounded-full bg-indigo-400"
                              style={{ width: `${(a.duration_seconds / max) * 100}%` }}
                            />
                          </div>
                          <span className="w-12 text-right text-xs tabular-nums text-slate-500">
                            {fmtSecs(a.duration_seconds)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="py-6 text-center text-sm text-slate-400">No allowed usage</p>
                )}
              </div>
            </section>

            <section className="att-fade-up space-y-3" style={{ animationDelay: '120ms' }}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Staff breakdown</h3>
                  <p className="text-sm text-slate-500">
                    Open a person to allow or block sites & apps
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search staff…"
                      className="h-9 w-44 rounded-xl border-slate-200 pl-8 text-sm"
                    />
                  </div>
                  {staffRows.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9 rounded-xl"
                      onClick={() => {
                        const csv = ['Staff,Total Time,Allowed,Blocked,Productivity%']
                          .concat(
                            staffRows.map(
                              (r) =>
                                `${r.staff_name},${fmtSecs(r.total_seconds)},${fmtSecs(r.allowed_seconds)},${fmtSecs(r.blocked_seconds)},${r.productivity_score?.toFixed(0) || '–'}%`
                            )
                          )
                          .join('\n');
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                        a.download = `usage-report-${fromDate}-${toDate}.csv`;
                        a.click();
                      }}
                    >
                      <Download className="mr-1 h-3.5 w-3.5" />
                      CSV
                    </Button>
                  )}
                </div>
              </div>

              {staffRows.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-slate-200 bg-white/80 px-6 py-14 text-center text-slate-500">
                  {search ? `No staff matching “${search}”` : 'No usage data for this range'}
                </div>
              ) : (
                <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-slate-100 bg-slate-50/80">
                        <tr>
                          {['Staff', 'Total', 'Allowed', 'Blocked', 'Score', 'Status', ''].map(
                            (h) => (
                              <th
                                key={h || 'a'}
                                className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-400"
                              >
                                {h}
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {staffRows.map((r) => (
                          <tr
                            key={r.staff_id}
                            className="cursor-pointer transition hover:bg-indigo-50/40"
                            onClick={() =>
                              navigate(
                                `/admin/usage-policy/staff/${r.staff_id}?date=${toDate}`
                              )
                            }
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2.5">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-xs font-bold text-white">
                                  {(r.staff_name || '?').charAt(0).toUpperCase()}
                                </div>
                                <span className="font-semibold text-slate-900">{r.staff_name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 font-semibold tabular-nums text-slate-800">
                              {fmtSecs(r.total_seconds)}
                            </td>
                            <td className="px-4 py-3 tabular-nums text-indigo-600">
                              {fmtSecs(r.allowed_seconds)}
                            </td>
                            <td className="px-4 py-3 tabular-nums text-rose-600">
                              {fmtSecs(r.blocked_seconds)}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="h-1.5 w-12 overflow-hidden rounded-full bg-slate-100">
                                  <div
                                    className="h-1.5 rounded-full bg-indigo-500"
                                    style={{
                                      width: `${Math.min(100, r.productivity_score || 0)}%`,
                                    }}
                                  />
                                </div>
                                <span className="text-xs font-semibold tabular-nums text-slate-600">
                                  {(r.productivity_score || 0).toFixed(0)}%
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {r.violations > 0 ? (
                                <Badge className="bg-rose-50 text-rose-700 ring-1 ring-rose-100">
                                  {r.violations} violation{r.violations === 1 ? '' : 's'}
                                </Badge>
                              ) : (
                                <Badge className="bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100">
                                  Clean
                                </Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 rounded-lg text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/admin/usage-policy/staff/${r.staff_id}?date=${toDate}`
                                  );
                                }}
                                data-testid={`details-btn-${r.staff_id}`}
                              >
                                Control
                                <ExternalLink className="ml-1 h-3 w-3" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
